import { useEffect, useState, type MouseEvent } from 'react';
import type { EventClient } from '@aicores/events';
import type { Bonus, PortalContent } from './types';
import { IconArrowRight, IconCheck, IconCopy, IconStar, IconTarget } from './icons';
import { PriorityFinder } from './PriorityFinder';
import { loadResult, type PriorityFinderResult } from './priority-finder-data';

interface Props {
  bonuses: Bonus[];
  events: EventClient;
  eyebrow?: string;
  title?: string;
  productId: string;
  content: PortalContent;
  copiedPromptIds: Set<string>;
  onBonusViewed?: (bonusId: string) => void;
  onAssessmentCompleted?: (bonusId: string) => void;
}

export function BonusSection({
  bonuses,
  events,
  eyebrow,
  title,
  productId,
  content,
  copiedPromptIds,
  onBonusViewed,
  onAssessmentCompleted,
}: Props) {
  return (
    <section className="bonuses">
      <div className="bonuses-header">
        <div className="bonuses-eyebrow">{eyebrow ?? 'Included with your engine'}</div>
        <h2 className="bonuses-title">{title ?? 'Bonuses'}</h2>
      </div>
      <div className="bonus-cards">
        {bonuses.map((bonus, i) => (
          <BonusCard
            key={bonus.bonus_id}
            bonus={bonus}
            events={events}
            index={i}
            productId={productId}
            content={content}
            copiedPromptIds={copiedPromptIds}
            onViewed={onBonusViewed}
            onAssessmentCompleted={onAssessmentCompleted}
          />
        ))}
      </div>
    </section>
  );
}

function BonusCard({
  bonus,
  events,
  index,
  productId,
  content,
  copiedPromptIds,
  onViewed,
  onAssessmentCompleted,
}: {
  bonus: Bonus;
  events: EventClient;
  index: number;
  productId: string;
  content: PortalContent;
  copiedPromptIds: Set<string>;
  onViewed?: (bonusId: string) => void;
  onAssessmentCompleted?: (bonusId: string) => void;
}) {
  const isFeature = bonus.feature === 'priority_finder';
  const isPrompt = !!bonus.prompt_text;

  const [expanded, setExpanded] = useState(false);
  const [pfOpen, setPfOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pfResult, setPfResult] = useState<PriorityFinderResult | null>(() =>
    isFeature && typeof window !== 'undefined' ? loadResult(productId, bonus.bonus_id) : null,
  );
  const [viewFired, setViewFired] = useState(false);

  useEffect(() => {
    if (!isFeature) return;
    const handler = () => setPfResult(loadResult(productId, bonus.bonus_id));
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [isFeature, productId, bonus.bonus_id]);

  function handleClick() {
    if (isFeature) {
      setPfOpen(true);
      if (!viewFired) {
        setViewFired(true);
        events.fire('bonus_viewed', { bonus_id: bonus.bonus_id });
        onViewed?.(bonus.bonus_id);
      }
      return;
    }
    const next = !expanded;
    setExpanded(next);
    if (next && !viewFired) {
      setViewFired(true);
      events.fire('bonus_viewed', { bonus_id: bonus.bonus_id });
      onViewed?.(bonus.bonus_id);
    }
  }

  async function copyPrompt(e: MouseEvent) {
    e.stopPropagation();
    if (!bonus.prompt_text) return;
    try {
      await navigator.clipboard.writeText(bonus.prompt_text);
    } catch {
      return;
    }
    setCopied(true);
    events.fire('bonus_copied', { bonus_id: bonus.bonus_id });
    window.setTimeout(() => setCopied(false), 1800);
  }

  const Icon = index % 2 === 0 ? IconTarget : IconStar;
  const label = `Bonus ${String(index + 1).padStart(2, '0')}`;
  const cleanTitle = bonus.title.replace(/^Bonus #\d+:\s*/, '');

  return (
    <>
      <article
        id={`bonus-${bonus.bonus_id}`}
        className={`bonus-card${expanded ? ' is-expanded' : ''}${isFeature ? ' is-feature' : ''}`}
        aria-expanded={isFeature ? undefined : expanded}
      >
        <button
          type="button"
          className="bonus-card-trigger"
          onClick={handleClick}
          aria-expanded={isFeature ? undefined : expanded}
        >
          <div className="bonus-icon" aria-hidden="true">
            <Icon />
          </div>
          <div className="bonus-content">
            <div className="bonus-label">{label}</div>
            <div className="bonus-title">{cleanTitle}</div>
            <div className="bonus-description">{bonus.description}</div>
            {isFeature && pfResult ? (
              <div className="bonus-last-result">
                <span className={`pf-pct-dot is-${pfResult.overall_rating}`} aria-hidden="true" />
                Last result: {pfResult.overall_pct}% on{' '}
                {new Date(pfResult.completed_at).toLocaleDateString()}
              </div>
            ) : null}
          </div>
          <div className="bonus-arrow" aria-hidden="true">
            <IconArrowRight />
          </div>
        </button>

        {isPrompt && expanded ? (
          <div className="bonus-body">
            {bonus.instructions ? (
              <div className="bonus-instructions">
                <div className="bonus-instructions-label">Before you start</div>
                <p>{bonus.instructions}</p>
              </div>
            ) : null}
            <div className="prompt-text-container">
              <div className="prompt-text-label">
                <div className="prompt-text-label-text">The prompt</div>
              </div>
              <div className="prompt-text">{bonus.prompt_text}</div>
            </div>
            <button type="button" className="btn btn-primary" onClick={copyPrompt}>
              {copied ? (
                <>
                  <IconCheck aria-hidden="true" /> Copied
                </>
              ) : (
                <>
                  <IconCopy aria-hidden="true" /> Copy prompt
                </>
              )}
            </button>
          </div>
        ) : null}

        {!isFeature && !isPrompt && expanded ? (
          <div className="bonus-body">
            <em className="bonus-placeholder">
              Content being finalised for launch. Check back before you go live with your email
              sequence.
            </em>
          </div>
        ) : null}
      </article>

      {isFeature ? (
        <PriorityFinder
          open={pfOpen}
          bonusId={bonus.bonus_id}
          productId={productId}
          content={content}
          events={events}
          previousResult={pfResult}
          copiedPromptIds={copiedPromptIds}
          onClose={() => setPfOpen(false)}
          onCompleted={() => {
            setPfResult(loadResult(productId, bonus.bonus_id));
            onAssessmentCompleted?.(bonus.bonus_id);
          }}
        />
      ) : null}
    </>
  );
}
